/** tm/legal/useNewsPopup.ts — 「새 소식」 팝업을 띄울지 결정 + PC 알림.
 *  · 아직 보여 주지 않은(first_seen_at > 본 시점) 알림이 있고, 「나중에」 중이 아니며, 탭이 보이면 띄운다.
 *  · 닫힘(자동 숨김·바로 보기·전체 보기·×)은 본 것으로 기록 → 같은 묶음은 다시 뜨지 않는다. 「나중에」는 2시간 뒤 다시.
 *  · 탭이 숨겨진 사이 새 알림이 오면(PC 알림 켬 + 권한 허용) 시스템 알림 — 누르면 창을 앞으로 가져와 그 글을 연다. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { entries, ranked, latestOf, navOf, hookOf, type AlertEntry } from './alertUtil';
import { getSeen, setSeen, getSnooze, setSnooze, SNOOZE_MS, getPcNotify, pcPermission } from './alertPrefs';
import type { LegalAlerts, LegalNavParams } from './legalTypes';

/** 대시보드 「오늘의 새 소식」 카드가 화면에 있는지 (있으면 팝업을 띄우지 않는다) */
let dashCards = 0;
export const dashboardShown = () => dashCards > 0;
export function useDashboardCardMounted(): void {
  useEffect(() => { dashCards += 1; return () => { dashCards -= 1; }; }, []);
}

export interface PopupState { list: AlertEntry[]; fresh: number; latest: string }

function useVisible(): boolean {
  const [v, setV] = useState(() => (typeof document === 'undefined' ? true : !document.hidden));
  useEffect(() => {
    const on = () => setV(!document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return v;
}

export function useNewsPopup(uid: string, data: LegalAlerts, onOpen: (p: LegalNavParams) => void) {
  const [pop, setPop] = useState<PopupState | null>(null);
  const [tick, setTick] = useState(0); // 「나중에」가 끝나면 다시 계산
  const visible = useVisible();
  const latest = latestOf(data);
  const prevLatest = useRef<string | null | undefined>(undefined);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  // 팝업 결정
  useEffect(() => {
    if (!latest || !visible || pop) return undefined;
    const seen = getSeen(uid);
    if (seen && Date.parse(latest) <= Date.parse(seen)) return undefined;
    const until = getSnooze(uid);
    if (until > Date.now()) {
      const t = setTimeout(() => setTick(x => x + 1), Math.min(until - Date.now() + 500, 2 ** 31 - 1));
      return () => clearTimeout(t);
    }
    const fresh = entries(data, seen);
    if (!fresh.length) { setSeen(uid, latest); return undefined; } // 이미 읽은 것만 남음
    if (dashboardShown()) { setSeen(uid, latest); return undefined; } // 대시보드 카드가 같은 글을 이미 보여 주는 중 → 팝업 없이 배지만
    setPop({ list: ranked(fresh, data).slice(0, 3), fresh: fresh.length, latest });
    return undefined;
  }, [uid, latest, visible, pop, data, tick]);

  // PC 알림 — 이 창이 숨겨진 사이 새로 들어온 것만 (첫 로드 제외)
  useEffect(() => {
    const before = prevLatest.current;
    prevLatest.current = latest;
    if (before === undefined || !latest || (before && Date.parse(latest) <= Date.parse(before))) return;
    if (!document.hidden || !getPcNotify() || pcPermission() !== 'granted') return;
    const fresh = ranked(entries(data, before), data);
    if (!fresh.length) return;
    const top = fresh[0];
    try {
      const n = new Notification(`새 소식 ${fresh.length}건 · 법령·판례·뉴스`, { body: `${top.it.title}\n${hookOf(top)}`, tag: 'tm-legal', silent: false });
      n.onclick = () => { window.focus(); openRef.current(navOf(top)); setSeen(uid, latest); n.close(); };
    } catch { /* 일부 브라우저(모바일)는 페이지에서 직접 알림을 만들 수 없다 */ }
  }, [latest, data, uid]);

  const close = useCallback((markSeen: boolean) => {
    setPop(cur => { if (cur && markSeen) setSeen(uid, cur.latest); return null; });
  }, [uid]);
  const later = useCallback(() => { setSnooze(uid, Date.now() + SNOOZE_MS); setPop(null); }, [uid]);
  return { pop, close, later };
}
